import React, { useState } from 'react';
import { CheckCircleIcon, AlertCircleIcon, XIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { BrandRequest } from '../../data/types';
interface ApproveModalProps {
  isOpen: boolean;
  request: BrandRequest | null;
  onClose: () => void;
  onApproveAsIs: (request: BrandRequest) => void;
  onApproveWithConditions: (request: BrandRequest, conditions: string) => void;
}
export function ApproveModal({
  isOpen,
  request,
  onClose,
  onApproveAsIs,
  onApproveWithConditions
}: ApproveModalProps) {
  const [mode, setMode] = useState<'choose' | 'conditions'>('choose');
  const [conditions, setConditions] = useState('');
  if (!isOpen || !request) return null;
  const handleClose = () => {
    setMode('choose');
    setConditions('');
    onClose();
  };
  const handleApproveWithConditions = () => {
    if (conditions.trim()) {
      onApproveWithConditions(request, conditions.trim());
      setMode('choose');
      setConditions('');
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in" data-id="element-46">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden animate-slide-up" data-id="element-47">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border" data-id="element-48">
          <div className="flex items-center gap-3" data-id="element-49">
            <div className="w-10 h-10 rounded-full bg-available/10 flex items-center justify-center" data-id="element-50">
              <CheckCircleIcon className="w-5 h-5 text-available" data-id="element-51" />
            </div>
            <div data-id="element-52">
              <h2 className="text-lg font-bold text-text-primary" data-id="element-53">
                Approve Request
              </h2>
              <p className="text-xs text-text-secondary" data-id="element-54">
                {request.brandName} ·{' '}
                {request.campaignName || 'Unnamed Campaign'}
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="text-text-tertiary hover:text-text-primary transition-colors" data-id="element-55">
            <XIcon className="w-5 h-5" data-id="element-56" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6" data-id="element-57">
          {mode === 'choose' ? <div className="space-y-3" data-id="element-58">
              <p className="text-sm text-text-secondary mb-4" data-id="element-59">
                How would you like to approve this request?
              </p>

              {/* Approve as-is */}
              <button onClick={() => {
            onApproveAsIs(request);
            handleClose();
          }} className="w-full flex items-start gap-4 p-4 border border-border rounded-lg hover:border-available hover:bg-available/5 transition-all text-left group" data-id="element-60">
                <div className="w-10 h-10 rounded-full bg-available/10 flex items-center justify-center flex-shrink-0 group-hover:bg-available/20 transition-colors" data-id="element-61">
                  <CheckCircleIcon className="w-5 h-5 text-available" data-id="element-62" />
                </div>
                <div data-id="element-63">
                  <div className="text-sm font-semibold text-text-primary" data-id="element-64">
                    Approve as-is
                  </div>
                  <div className="text-xs text-text-secondary mt-0.5" data-id="element-65">
                    Accept the request exactly as submitted. The brand will be
                    notified immediately.
                  </div>
                </div>
              </button>

              {/* Approve with conditions */}
              <button onClick={() => setMode('conditions')} className="w-full flex items-start gap-4 p-4 border border-border rounded-lg hover:border-requested hover:bg-requested/5 transition-all text-left group" data-id="element-66">
                <div className="w-10 h-10 rounded-full bg-requested/10 flex items-center justify-center flex-shrink-0 group-hover:bg-requested/20 transition-colors" data-id="element-67">
                  <AlertCircleIcon className="w-5 h-5 text-requested" data-id="element-68" />
                </div>
                <div data-id="element-69">
                  <div className="text-sm font-semibold text-text-primary" data-id="element-70">
                    Approve with conditions
                  </div>
                  <div className="text-xs text-text-secondary mt-0.5" data-id="element-71">
                    Accept the request but specify changes or conditions the
                    brand must follow.
                  </div>
                </div>
              </button>
            </div> : <div className="space-y-4" data-id="element-72">
              <div className="flex items-center gap-2 text-sm text-text-secondary" data-id="element-73">
                <AlertCircleIcon className="w-4 h-4 text-requested" data-id="element-74" />
                Specify the conditions for approval
              </div>

              <textarea value={conditions} onChange={e => setConditions(e.target.value)} placeholder="e.g. Approved for Sandton City only — V&A Waterfront unavailable for Week 4. Please confirm updated artwork by 20 Mar." className="w-full h-32 px-4 py-3 text-sm border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary placeholder:text-text-tertiary" autoFocus data-id="element-75" />

              <div className="bg-surface-warm border border-border rounded-lg p-3" data-id="element-76">
                <p className="text-xs text-text-secondary" data-id="element-77">
                  <span className="font-semibold" data-id="element-78">Note:</span> The brand will
                  see these conditions alongside their approval notification.
                  The request will be marked as "Approved with Conditions".
                </p>
              </div>
            </div>}
        </div>

        {/* Footer */}
        {mode === 'conditions' && <div className="flex items-center justify-between px-6 py-4 bg-surface-warm border-t border-border" data-id="element-79">
            <Button variant="ghost" size="sm" onClick={() => {
          setMode('choose');
          setConditions('');
        }} data-id="element-80">
              ← Back
            </Button>
            <div className="flex items-center gap-2" data-id="element-81">
              <Button variant="secondary" size="sm" onClick={handleClose} data-id="element-82">
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={handleApproveWithConditions} disabled={!conditions.trim()} className="bg-available hover:bg-available/90" data-id="element-83">
                Approve with Conditions
              </Button>
            </div>
          </div>}
      </div>
    </div>;
}